/**
 * Clock abstraction for time-related operations.
 * Enables deterministic testing by allowing time to be controlled in tests.
 */

/**
 * Interface for clock operations.
 */
export interface Clock {
    /**
     * Returns the current time in milliseconds since epoch.
     */
    now(): number
}

/**
 * System clock implementation using Date.now().
 * Use this in production code.
 */
export const systemClock: Clock = {
    now: () => Date.now(),
}

/**
 * Factory function to create a test clock with controllable time.
 * Use this in tests to control time progression.
 *
 * @param initialTime - The initial time value (defaults to 0)
 * @returns A Clock instance with additional advance() and set() methods
 */
export const createTestClock = (
    initialTime: number = 0
): Clock & {
    advance: (ms: number) => void
    set: (time: number) => void
    getTime: () => number
} => {
    let currentTime = initialTime

    return {
        now: () => currentTime,
        advance: (ms: number) => {
            currentTime += ms
        },
        set: (time: number) => {
            currentTime = time
        },
        getTime: () => currentTime,
    }
}
